import { initConnectedAppsI18next } from "@domains/connected-apps/client/i18n/i18next.ts";
import { ChartBarIcon, ClipboardDocumentListIcon, RectangleStackIcon, UserIcon } from "@heroicons/react/24/outline";
import { REVIEW_CYCLE_CREATE_ACCESS } from "@packages/core/access/settingsAccess.ts";
import { hasFeature } from "@packages/core/session/features.ts";
import { hasAnyRole } from "@packages/core/session/roles.ts";
import type { Session } from "@packages/core/session/session.ts";
import { NavigationItemLabel } from "@packages/core-client/i18n/NavigationItemLabel.tsx";
import { getCommandMenuLinksRegistryPlugin } from "@packages/core-client/registration/command-menu/CommandMenuLinksPlugin.ts";
import type { AppModuleRegisterFunction } from "@packages/core-client/registration/appModuleRegisterFunction.ts";
import { registerCreateMenuItem } from "@packages/core-client/registration/createMenu.tsx";
import type { LinkAdditionalProps } from "@packages/core-client/registration/link.ts";
import { TOP_MENU_ID } from "@packages/core-client/registration/menuIds.ts";
import { buildPersonPath } from "@packages/core-client/routing/personPath.ts";
import { ProtectedRoutes, PublicRoutes, type FireflyRuntime } from "@squide/firefly";
import type { i18n } from "i18next";
import { OFFICEVIBE_SURVEY_CREATE_ACCESS } from "#shared/access.ts";
import { NotFoundPage } from "./pages/NotFoundPage.tsx";
import { RootLayout } from "./layouts/RootLayout.tsx";
import { initI18next } from "./i18next.ts";
import { captureUtmParameters } from "./utmParameters.ts";

async function registerMsw(runtime: FireflyRuntime) {
    if (runtime.isMswEnabled) {
        const requestHandlers = (await import("./api/mocks/getRequestHandlers.ts")).getRequestHandlers();

        runtime.registerRequestHandlers(requestHandlers);

        const { failureRequestHandler } = await import("./api/mocks/failure/failureRequestHandler.ts");

        runtime.registerRequestHandlers([failureRequestHandler], { prepend: true });
    }
}

function registerRoutes(runtime: FireflyRuntime) {
    runtime.registerPublicRoute(
        {
            $id: "root-layout",
            element: <RootLayout />,
            children: [PublicRoutes]
        },
        {
            hoist: true
        }
    );

    runtime.registerRoute(
        {
            lazy: async () => {
                const { AuthenticationBoundary } = await import("./boundaries/AuthenticationBoundary.tsx");

                return {
                    element: <AuthenticationBoundary />
                };
            },
            children: [
                {
                    lazy: async () => {
                        const { AuthenticatedLayout } = await import("./layouts/AuthenticatedLayout.tsx");

                        return {
                            element: <AuthenticatedLayout />
                        };
                    },
                    children: [
                        {
                            lazy: async () => {
                                const { ModuleErrorBoundary } = await import("./boundaries/ModuleErrorBoundary.tsx");

                                return {
                                    errorElement: <ModuleErrorBoundary />
                                };
                            },
                            children: [
                                {
                                    lazy: async () => {
                                        const { LegacyPeopleRedirectBoundary } = await import("./boundaries/LegacyPeopleRedirectBoundary.tsx");

                                        return {
                                            element: <LegacyPeopleRedirectBoundary />
                                        };
                                    },
                                    children: [
                                        {
                                            lazy: async () => {
                                                const { AuthorizationBoundary } = await import("./boundaries/AuthorizationBoundary.tsx");

                                                return {
                                                    element: <AuthorizationBoundary />
                                                };
                                            },
                                            children: [
                                                {
                                                    lazy: async () => {
                                                        const { AuthenticatedPageLayout } = await import("./layouts/AuthenticatedPageLayout.tsx");

                                                        return {
                                                            element: <AuthenticatedPageLayout />
                                                        };
                                                    },
                                                    children: [ProtectedRoutes]
                                                }
                                            ]
                                        }
                                    ]
                                }
                            ]
                        }
                    ]
                }
            ]
        },
        {
            parentId: "root-layout"
        }
    );

    runtime.registerRoute({
        path: "/people/*",
        element: <NotFoundPage />
    });

    runtime.registerRoute({
        path: "/org/employees/*",
        element: <NotFoundPage />
    });

    runtime.registerPublicRoute({
        path: "*",
        element: <NotFoundPage />
    });
}

const ME_MENU_ITEM_ID = `${TOP_MENU_ID}-me`;
const NAVIGATION_NAMESPACE = "Navigation";

function registerMeMenuItem(runtime: FireflyRuntime, i18nextInstance: i18n, session: Session | undefined) {
    if (!session?.memberId) {
        return;
    }

    const to = buildPersonPath(session.memberId);
    const label = { instance: i18nextInstance, namespace: NAVIGATION_NAMESPACE, resourceKey: "me.label" };

    runtime.registerNavigationItem(
        {
            $id: ME_MENU_ITEM_ID,
            $priority: 250,
            $label: <NavigationItemLabel {...label} />,
            to,
            $additionalProps: { icon: UserIcon } satisfies LinkAdditionalProps
        },
        { menuId: TOP_MENU_ID }
    );

    getCommandMenuLinksRegistryPlugin(runtime)?.registerLink({
        id: ME_MENU_ITEM_ID,
        to,
        groupId: "me",
        icon: UserIcon,
        priority: 250,
        menuId: TOP_MENU_ID,
        i18next: {
            instance: i18nextInstance,
            namespace: NAVIGATION_NAMESPACE,
            labelKey: "me.label",
            descriptionKey: "me.description",
            parentLabelKey: "",
            keywordsKey: "",
            groupLabelKey: "me.label"
        }
    });
}

function registerCreateMenuRedirects(runtime: FireflyRuntime, i18nextInstance: i18n, session: Session | undefined) {
    const label = (resourceKey: string) => ({ instance: i18nextInstance, namespace: "CreateMenu", resourceKey });

    if (hasAnyRole(session, OFFICEVIBE_SURVEY_CREATE_ACCESS)) {
        registerCreateMenuItem(runtime, {
            id: "survey",
            label: label("newSurvey"),
            icon: ChartBarIcon,
            // oxlint-disable-next-line typescript/restrict-template-expressions -- Squide 19.2.1's class d.ts types getEnvironmentVariable() as never; the registry holds a string
            to: `${runtime.getEnvironmentVariable("officevibeUrl")}/portal/custom-polls/active/create/new`,
            priority: 20,
            isExternal: true
        });
    }

    if (hasAnyRole(session, REVIEW_CYCLE_CREATE_ACCESS)) {
        registerCreateMenuItem(runtime, {
            id: "review-cycle",
            label: label("newReviewCycle"),
            icon: ClipboardDocumentListIcon,
            to: "/org/review-cycles/new",
            priority: 10
        });
    }

    if (runtime.getFeatureFlag("plans", false) && hasFeature("Plans")(session)) {
        registerCreateMenuItem(runtime, {
            id: "plan",
            label: label("newPlan"),
            icon: RectangleStackIcon,
            to: "/plans/new",
            priority: 30
        });
    }
}

export const registerShell: AppModuleRegisterFunction = async runtime => {
    captureUtmParameters(window.location.search);
    const i18nextInstance = initI18next(runtime);
    initConnectedAppsI18next(runtime);
    await registerMsw(runtime);
    registerRoutes(runtime);

    return (deferredRuntime, { session }) => {
        registerMeMenuItem(deferredRuntime, i18nextInstance, session);
        registerCreateMenuRedirects(deferredRuntime, i18nextInstance, session);
    };
};
